import { Component, OnDestroy } from "@angular/core";
import { FormControl, Validators } from "@angular/forms";
import { MatDialogRef } from "@angular/material/dialog";
import { Subject } from "rxjs";
import { finalize, takeUntil } from "rxjs/operators";
import { DigitalCardService } from "../digital-card.service";
import type { DigitalCardCustomSourceItem } from "../digital-cards.interface";

@Component({
  selector: "app-digital-card-source-create-dialog",
  templateUrl: "./digital-card-source-create-dialog.component.html",
  styleUrls: ["./digital-card-source-create-dialog.component.scss"],
  standalone: false,
})
export class DigitalCardSourceCreateDialogComponent implements OnDestroy {
  readonly nameControl = new FormControl("", {
    nonNullable: true,
    validators: [
      (c) =>
        (typeof c.value === "string" ? c.value.trim() : "").length > 0
          ? null
          : { required: true },
      Validators.maxLength(100),
    ],
  });
  creating = false;
  errorKey: string | null = null;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private dialogRef: MatDialogRef<
      DigitalCardSourceCreateDialogComponent,
      DigitalCardCustomSourceItem | null
    >,
    private digitalCardService: DigitalCardService,
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get canSubmit(): boolean {
    return !this.creating && !!this.nameControl.value.trim();
  }

  close(): void {
    this.dialogRef.close(null);
  }

  submit(): void {
    const name = this.nameControl.value.trim().replace(/\s+/g, " ");
    if (!name || this.creating) return;
    this.nameControl.setValue(name, { emitEvent: false });
    this.creating = true;
    this.errorKey = null;
    this.digitalCardService
      .createCustomSource(name)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.creating = false;
        }),
      )
      .subscribe({
        next: (created) => this.dialogRef.close(created),
        error: (err: { status?: number }) => {
          this.errorKey =
            err?.status === 409
              ? "digitalCards.sources.nameExists"
              : "digitalCards.sources.createFailed";
        },
      });
  }
}
